import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { AvailableLanguages } from "utils/types";

type AppLanguageContextValue = {
  language: AvailableLanguages | undefined;
  setLanguage: (lang: AvailableLanguages) => void;
  clearLanguage: () => void;
};

const STORAGE_KEY = "around-the-world.language";

const readStoredLanguage = (): AvailableLanguages | undefined => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === AvailableLanguages.EN || raw === AvailableLanguages.ES) return raw;
    return undefined;
  } catch {
    return undefined;
  }
};

const writeStoredLanguage = (lang: AvailableLanguages | undefined) => {
  try {
    if (!lang) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // ignore (private mode, storage disabled, etc.)
  }
};

const AppLanguageContext = createContext<AppLanguageContextValue | undefined>(
  undefined
);

export const AppLanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<AvailableLanguages | undefined>(
    () => readStoredLanguage()
  );

  const setLanguage = useCallback((lang: AvailableLanguages) => {
    setLanguageState(lang);
    writeStoredLanguage(lang);
  }, []);

  const clearLanguage = useCallback(() => {
    setLanguageState(undefined);
    writeStoredLanguage(undefined);
  }, []);

  const value = useMemo(
    () => ({ language, setLanguage, clearLanguage }),
    [language, setLanguage, clearLanguage]
  );

  return (
    <AppLanguageContext.Provider value={value}>
      {children}
    </AppLanguageContext.Provider>
  );
};

export const useAppLanguage = () => {
  const ctx = useContext(AppLanguageContext);
  if (!ctx) throw new Error("useAppLanguage must be used within AppLanguageProvider");
  return ctx;
};

